import { FunctionComponent as F } from "react";
import { useSelector } from 'react-redux';
import { IBundleSaving, DosingRowData } from "@protoplan/types";
import { IState } from "../../core/store/store";
import Stack from '@mui/material/Stack';
import { RowGroup } from "./CostBreakdownRowGroup";
import CostBreakdownDescr from "./CostBreakdownDescr";
import CostBreakdownTable from "./CostBreakdownTable";
import UpdateTax from "../UI/Button/UpdateTax";
import { formUnitCostRows } from "./unitCostRows";
import priceUpdateStatusLines from "../Listings/priceUpdateStatusLines";
import UpdatePrice from "../UI/Button/UpdatePrice";
import units from "../../core/units";



interface Props
{
  protocolRow: DosingRowData,
  bundleSaving: IBundleSaving;
  savingType: string;
}


const BundleCostBreakdown: F<Props> = (props: Props) =>
{
  const user = useSelector((state: IState) => state.user);
  const bundle = props.bundleSaving;
  const bundleListing = bundle.bundle[0];
  const bundleCurrencySymbol = bundleListing.listingCurrencySymbol;
  const bundleName = bundleListing.listingName;
  const bundleTaxPercent = bundleListing.taxPercent;
  const fees = bundleListing.costPerMonthWithFees - bundleListing.costPerMonthWithoutFees;

  let rowGroups: RowGroup[] = [
    // Unit cost
    {
      item: 'Bundle cost',
      value: user.currencySymbol + " " + bundleListing.priceWithFees.toFixed(2),
      rows: formUnitCostRows(bundleListing, user.currencySymbol, true, false) },
    // Listings per month (determined from products per month)
    {
      item: 'Bundles per month (at current dose)',
      value: bundleListing.listingsPerMonth.toPrecision(2),
      rows: bundle.replaceableRows.map(row => ([
        {
          item: 'Dose per month',
          value: (row.dose * row.dosesPerDay * row.daysPerMonth).toPrecision(3) + ' ' + row.doseUnit },
        {
          item: 'Unit conversion factor (' + row.doseUnit + '->' + row.amountUnit + ')',
          value: '*' + units.getFactor(row.doseUnitId, row.amountUnitId, [row.productId]),
          tooltip: ['Multiplication factor to convert ' + row.doseUnit + ' to ' + row.amountUnit],
          show: Boolean(row.doseUnitId !== row.amountUnitId) },
        { item: 'Amount per product', value: row.amount + ' ' + row.amountUnit},
        {
          item: 'Units per month of ' + row.productName,
          value: row.productsPerMonth.toFixed(2) },
        {
          item: 'Number of ' + props.protocolRow.productName + ' in bundle',
          value: bundle.bundle.find(p => p.productId === row.productId)?.quantity }])).flat() },
    // Fees
    {
      item: 'Fees per month',
      value: user.currencySymbol + " " + fees.toFixed(2),
      show: Boolean(fees),
      rows: [
        {
          item: 'Basket limit' + (bundleListing.basketLimitEstimated ? ' (est.)' : ''),
          value: user.currencySymbol + " " + bundleListing.basketLimit.toFixed(0) },
        {
          item: 'Max. units per order' + (bundleListing.basketLimitEstimated ? ' (est.)' : ''),
          value: bundleListing.maxListingsPerOrder },
        { item: 'Orders per month', value: bundleListing.ordersPerMonth.toFixed(1) },
        {
          item: 'Delivery' + (bundleListing.deliveryPriceEstimated ? ' (est.)' : ''),
          value: bundleCurrencySymbol + " " + bundleListing.deliveryPrice,
          show: Boolean(bundleListing.deliveryPrice) },
        { item: 'Tax' + (bundleListing.taxEstimated ? ' (est.)' : ''),
          value:
            user.currencySymbol + " " + (bundleListing.baseTax || 0).toFixed(2) +
            (bundleTaxPercent ? " + " + bundleTaxPercent + "%" : ""),
          valueSymbol: <UpdateTax listing={bundleListing} vendorId={bundleListing.vendorId} size={13}/>,
          tooltip:
            ['Fixed baseline tax added to each order,' +
            (bundleTaxPercent ? ' in addition to the % tax charged on the product price' : '')],
          show: Boolean(bundleListing.baseTax || bundleTaxPercent) }] },
    // Extra individual products needed
    {
      item: 'Extra out-of-bundle products cost',
      value: user.currencySymbol + " " + (
        bundle.leftoverProductsCostPerMonth +
        bundle.leftoverProductsFeesPerMonth).toFixed(2),
      tooltip: ['Extra individual products are required on top of bundle to meet your current dose of: ' +
        bundle.leftoverProducts.map(p => p.productName).join(', ')],
      rows: bundle.leftoverProducts.map(p => ([
        {
          item: 'Price with tax for individual ' + p.productName,
          value: p.listingCurrencySymbol + " " + p.priceWithFees.toFixed(2),
          tooltip: p.scrapeTime ? priceUpdateStatusLines(p.scrapeTime) : [''],
          valueSymbol: <UpdatePrice listingId={p.listingId} size={13} /> },
        {
          item: 'Dose per month',
          value: (p.dose * p.dosesPerDay * p.daysPerMonth).toPrecision(3) + ' ' + p.doseUnit },
        {
          item: 'Unit conversion factor (' + p.doseUnit + '->' + p.amountUnit + ')',
          value: '*' + units.getFactor(p.doseUnitId, p.amountUnitId, [p.productId]),
          tooltip: ['Multiplication factor to convert ' + p.doseUnit + ' to ' + p.amountUnit],
          show: Boolean(p.doseUnitId !== p.amountUnitId) },
        { item: 'Amount per product', value: p.amount + ' ' + p.amountUnit },
        {
          item: 'Total units per month of ' + p.productName,
          value: (
            Number(bundle.bundle.find(b => b.productId === p.productId)?.productsPerMonth) +
            p.productsPerMonth).toFixed(2) },
        {
          item: 'Units per month of ' + p.productName + ' in bundle',
          value: bundle.bundle.find(b => b.productId === p.productId)?.productsPerMonth?.toFixed(2) },
        {
          item: 'Units per month of indivudual ' + p.productName,
          value: p.productsPerMonth.toFixed(2) },
        {
          item: 'Fees per month for individual ' + p.productName,
          value: p.listingCurrencySymbol + " " + (
            p.costPerMonthWithFees -
            p.costPerMonthWithoutFees).toFixed(2) } ])).flat(),
      show: Boolean(bundle.leftoverProducts?.length) },
    // Cost per month
    {
      item: 'Cost per month',
      value: user.currencySymbol + " " + (
        bundle.bundleCostPerMonth +
        bundle.bundleFeesPerMonth +
        bundle.leftoverProductsCostPerMonth +
        bundle.leftoverProductsFeesPerMonth).toFixed(2),
      tooltip: ['Including fees'],
      bold: true }];

  if(bundle.bundleSaving)
  {
    rowGroups.push({
      item: 'Saving',
      value: user.currencySymbol + " " + bundle.bundleSaving.toFixed(2),
      tooltip:
        ["When replacing individual " + bundle.replaceableRows.map(r => r.productName).join(", ") + " " +
        "with " + bundleName],
      bold: true,
      valueColor: 'green' });
  }

  return (
    <Stack spacing={1.5} sx={{ width: '100%' }}>
      <CostBreakdownDescr rows={bundle.bundle} savingType={props.savingType} />
      <CostBreakdownTable rowGroups={rowGroups} saving={bundle.bundleSaving}/>
    </Stack>);
};

export default BundleCostBreakdown;
